import type { SsdDestinationFeed } from "@/lib/ssdSalesClient";
import { publicAppOrigin } from "@/lib/publicAppOrigin";
import { ghostBtnClass, inputClass } from "@/components/common/surfaceClasses";

function feedUrl(path: string, token?: string | null): string {
  const origin = publicAppOrigin();
  if (token) return `${origin}${path}?token=${encodeURIComponent(token)}`;
  return `${origin}${path}`;
}

function FeedRow(props: {
  label: string;
  destination: "google" | "meta";
  feed: SsdDestinationFeed | undefined;
  token: string | null;
  rotating: boolean;
  onRotate: () => void;
}) {
  const url = feedUrl(props.feed?.path ?? `/api/ssd/feed/${props.destination}`, props.token);
  return (
    <div className="min-w-0 space-y-2 rounded-xl border border-gray-100 p-3 dark:border-zinc-800">
      <div className="flex min-w-0 flex-wrap items-center justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900 dark:text-zinc-100">{props.label}</p>
        <button type="button" className={ghostBtnClass} disabled={props.rotating} onClick={props.onRotate}>
          {props.rotating ? "Saving…" : props.feed ? "Rotate token" : "Enable feed"}
        </button>
      </div>
      {props.feed ? (
        <p className="text-xs text-gray-600 dark:text-zinc-400">
          Active · …{props.feed.token_suffix}
          {props.feed.last_fetched_at
            ? ` · last pull ${new Date(props.feed.last_fetched_at).toLocaleString()}`
            : " · not pulled yet"}
          {props.feed.fetch_count ? ` · ${props.feed.fetch_count} pull${props.feed.fetch_count === 1 ? "" : "s"}` : ""}
        </p>
      ) : (
        <p className="text-xs text-gray-500 dark:text-zinc-400">No hosted URL yet. Enable to mint a pull token.</p>
      )}
      <label className="block min-w-0 text-xs text-gray-600 dark:text-zinc-400">
        Pull URL
        <input className={`mt-1 ${inputClass} font-mono text-xs`} readOnly value={url} />
      </label>
      {props.token ? (
        <p className="text-xs text-amber-800 dark:text-amber-200">
          Copy this URL now — the full token is shown only once after enable/rotate. Paste it into Google Ads Data
          Manager or Meta as the scheduled file URL.
        </p>
      ) : null}
      <button
        type="button"
        className={ghostBtnClass}
        disabled={!props.feed && !props.token}
        onClick={() => void navigator.clipboard.writeText(url)}
      >
        Copy URL
      </button>
    </div>
  );
}

export function AudienceDataHostedFeedCard(props: {
  feeds: SsdDestinationFeed[];
  tokens: Partial<Record<"google" | "meta", string>>;
  rotating: "google" | "meta" | null;
  onRotate: (destination: "google" | "meta") => void;
}) {
  const google = props.feeds.find((f) => f.destination === "google");
  const meta = props.feeds.find((f) => f.destination === "meta");
  return (
    <div className="min-w-0 space-y-3">
      <p className="text-sm text-gray-600 dark:text-zinc-300">
        Hosted pulls (last 90 days, same hashed CSV as download). Treat the token like a password — the full URL is
        shown only once after enable or rotate. Paste into Google Ads Data Manager / Meta as the scheduled file URL.
        URLs use <code className="text-xs">VITE_APP_URL</code> when set (target{" "}
        <code className="text-xs">https://app.turbosignco.com</code>).
      </p>
      <FeedRow
        label="Google"
        destination="google"
        feed={google}
        token={props.tokens.google ?? null}
        rotating={props.rotating === "google"}
        onRotate={() => props.onRotate("google")}
      />
      <FeedRow
        label="Meta"
        destination="meta"
        feed={meta}
        token={props.tokens.meta ?? null}
        rotating={props.rotating === "meta"}
        onRotate={() => props.onRotate("meta")}
      />
    </div>
  );
}
